import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "../../theme/color";
import fonts from "../../theme/fonts";
import LineConnector from "./LineConnector";
import { FaCheck } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setActiveSubStep,
  setActiveStep,
} from "../../store/onBoarding/onBoardingSlice";
import { RootState } from "../../store/rootReducer";

export type FieldType = "input" | "select" | "textArea" | "radio";
export type InputType = "text" | "tel" | "number" | "email" | "file" | "date";
export interface RadioOption {
  label: string;
  value: string;
  subText?: string;
}

export interface FormFields {
  name: string;
  label?: string;
  value?: string;
  type: FieldType;
  options?: string[];
  radioOption?: RadioOption;
  placeholder?: string;
  info?: string;
  icon?: string;
  inputType?: InputType;
  disabled?: boolean;
  notRequired?: boolean;
}
export interface SubStep {
  label: string;
  path?: string;
  subTitle: string;
  secondSubTitle?: string;
  formFields: Array<FormFields>;
  hasOneCol?: boolean;
  buttonText?: string;
}

export interface StepData {
  label: string;
  path?: string;
  subSteps?: SubStep[];
}

interface StepperProps {
  steps: StepData[];
}

const StepperComponent: React.FC<StepperProps> = ({ steps }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { activeStep, activeSubStep } = useSelector(
    (state: RootState) => state.OnBoarding
  );

  const getActiveStep = (path: string) => location.pathname.includes(path);

  const handleClickSubStep = (path: string, index: number) => {
    dispatch(setActiveSubStep(index));
    navigate(path);
  };
  const handleClickStep = (index: number) => {
    dispatch(setActiveStep(index));
    dispatch(setActiveSubStep(1));
    navigate(steps[index - 1]?.subSteps?.[0]?.path as string);
  };

  return (
    <StepperCont>
      {steps?.map((step, index) => {
        const isActiveStep = activeStep === index + 1;
        const isComplete = activeStep > index + 1;
        const totalSubSteps = step.subSteps?.length || 0;

        return (
          <div key={index}>
            <StepWrapper>
              <div>
                <StepCircle
                  $active={isActiveStep || isComplete}
                  onClick={() => handleClickStep(index + 1)}
                >
                  {isComplete ? <FaCheck /> : index + 1}
                </StepCircle>
              </div>
              <StepLabel $active={isActiveStep}>{step.label}</StepLabel>
            </StepWrapper>
            {isActiveStep && (
              <SubStepContainer>
                <LineConnector
                  totalSubSteps={totalSubSteps}
                  activeSubStep={activeSubStep || 0}
                />
                {step.subSteps && (
                  <SubStepCont>
                    {step.subSteps.map((subStep, subIndex) => {
                      const isActiveSubStep = activeSubStep === subIndex + 1;
                      const isSubStepComplete =
                        activeStep > index + 1 || isActiveSubStep;

                      return (
                        <SubStepWrapper
                          key={subIndex}
                          onClick={() =>
                            handleClickSubStep(
                              subStep.path as string,
                              subIndex + 1
                            )
                          }
                        >
                          <SubStepLabel
                            $active={getActiveStep(subStep.path as string)}
                          >
                            {subStep.label}
                          </SubStepLabel>
                        </SubStepWrapper>
                      );
                    })}
                  </SubStepCont>
                )}
              </SubStepContainer>
            )}
          </div>
        );
      })}
    </StepperCont>
  );
};

export default StepperComponent;

const StepperCont = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  @media only screen and (max-width: 1024px) {
    gap: 0.5rem;
  }
`;

const StepCircle = styled.div<{ $active: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ $active }) =>
    $active ? colors.secondary.islamicGreen : colors.neutral.silverFoil};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  color: ${colors.White};
  cursor: pointer;
  font-size: 14px; // Adjust size as needed for the icon
`;

const StepLabel = styled.span<{ $active: boolean }>`
  font-size: 1rem;
  white-space: nowrap;
  color: ${({ $active }) =>
    $active ? colors.neutral.smokyBlack : colors.neutral.oldSilver};
  font-weight: ${({ $active }) =>
    $active ? fonts.fontWeight.medium : fonts.fontWeight.regular};
  @media only screen and (max-width: 768px) {
    display: none;
  }
  @media only screen and (max-width: 1024px) {
    font-size: 0.8rem;
  }
`;

const SubStepCont = styled.div`
  margin-left: 1rem;
  @media only screen and (max-width: 768px) {
    display: none;
  }
`;

const SubStepWrapper = styled.div`
  margin: 0.5rem 0;
  cursor: pointer;
`;

const SubStepContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: 0.6rem;
  @media screen and (min-width: 1024px) and (min-width: 1024px) {
  }
`;

const SubStepLabel = styled.span<{ $active: boolean }>`
  font-size: 0.875rem;
  color: ${({ $active }) =>
    $active ? colors.secondary.islamicGreen : colors.neutral.oldSilver};
  font-weight: ${({ $active }) =>
    $active ? fonts.fontWeight.medium : fonts.fontWeight.regular};
  @media only screen and (max-width: 1024px) {
    font-size: 0.7rem;
  }
`;
